import { parseISO, format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Banknote, BadgeAlert, CalendarClock, CalendarDays, FileText, Gift, Scale, Thermometer, TrendingUp } from 'lucide-react';
import type { BelangrijkeDag } from '../lib/api';
import { cn, formatMoney } from '../lib/utils';
import { Card, ICON } from './ui';

/** Icoon en kleur per soort belangrijke dag. Geld = groen (zoals betaaldagen), de rest amber. */
export const DAG_META: Record<BelangrijkeDag['kind'], { icon: typeof Banknote; text: string; dot: string }> = {
  eindejaarsuitkering: { icon: Gift, text: 'text-money', dot: 'bg-money' },
  vakantiegeld: { icon: Banknote, text: 'text-money', dot: 'bg-money' },
  loonsverhoging: { icon: TrendingUp, text: 'text-brand-300', dot: 'bg-brand-400' },
  kwalificatie: { icon: BadgeAlert, text: 'text-brand-300', dot: 'bg-brand-400' },
  verlof: { icon: CalendarClock, text: 'text-brand-300', dot: 'bg-brand-400' },
  contract: { icon: FileText, text: 'text-slate-300', dot: 'bg-slate-400' },
  cao: { icon: Scale, text: 'text-slate-300', dot: 'bg-slate-400' },
  ziek: { icon: Thermometer, text: 'text-rose-300', dot: 'bg-rose-400' },
};

const label = (d: BelangrijkeDag) => `${d.title}${d.bedrag != null ? ` ${formatMoney(d.bedrag)}` : ''}`;

/** Klein icoon + label in een dagcel (desktop). */
export function DagLabel({ d }: { d: BelangrijkeDag }) {
  const m = DAG_META[d.kind];
  const Icon = m.icon;
  return (
    <div
      className={cn('flex min-w-0 items-center gap-1 text-xs leading-tight', m.text)}
      title={`${label(d)}${d.verwacht ? ' (verwacht)' : ''}${d.detail ? `\n${d.detail}` : ''}`}
    >
      <Icon className={cn(ICON.sm, 'shrink-0')} aria-hidden />
      <span className="truncate">{d.title}</span>
    </div>
  );
}

/** Regel in een lijst (mobiele weekagenda). */
export function DagRegel({ d }: { d: BelangrijkeDag }) {
  const m = DAG_META[d.kind];
  const Icon = m.icon;
  return (
    <div className={cn('flex min-w-0 items-center gap-2 text-sm', m.text)}>
      <Icon className={cn(ICON.sm, 'shrink-0')} aria-hidden />
      <span className="min-w-0 truncate">{label(d)}</span>
      {d.verwacht && <span className="status-muted shrink-0">verwacht</span>}
    </div>
  );
}

/** Belangrijke dagen in de getoonde maand, altijd zichtbaar. */
export function BelangrijkeDagenLijst({ dagen, titel }: { dagen: BelangrijkeDag[]; titel: string }) {
  if (!dagen.length) return null;
  const today = format(new Date(), 'yyyy-MM-dd');
  return (
    <Card flush title={titel} icon={CalendarDays}>
        <ul className="list border-t border-surface-line px-4 sm:px-5">
          {dagen.map((d, i) => {
            const m = DAG_META[d.kind];
            const Icon = m.icon;
            return (
              <li key={`${d.date}-${i}`} className={cn('flex gap-3 py-2.5', d.date < today && 'opacity-60')}>
                <span className="t-num w-16 shrink-0 text-sm text-slate-400">{format(parseISO(d.date), 'EEE d MMM', { locale: nl })}</span>
                <Icon className={cn(ICON.sm, 'mt-0.5 shrink-0', m.text)} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm text-slate-100">{d.title}</span>
                    {d.bedrag != null && <span className={cn('t-num text-sm font-medium', m.text)}>{formatMoney(d.bedrag)}</span>}
                    {d.verwacht && <span className="status-muted">verwacht</span>}
                  </div>
                  {d.detail && <p className="mt-0.5 text-xs text-slate-400">{d.detail}</p>}
                </div>
              </li>
            );
          })}
        </ul>
    </Card>
  );
}
